import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FileText, Plus, Search, Trash2, Printer, Download, ChevronLeft, Eye, X, Award, CheckCircle, Recycle, Mail, Settings, Users, Edit3 } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { useAuth } from '../contexts/AuthContext';
import api from '../api';

// Logo URL for print window
const LOGO_URL = '/logo-light.png';

const statusColors = {
  draft: 'bg-gray-100 text-gray-700',
  sent: 'bg-blue-100 text-blue-700',
  paid: 'bg-green-100 text-green-700',
  cancelled: 'bg-red-100 text-red-700',
};

export default function InvoicesPage() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [view, setView] = useState('list'); // list | create | preview
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [previewInvoice, setPreviewInvoice] = useState(null);
  const [editingInvoice, setEditingInvoice] = useState(null); // for editing existing invoices

  // VIN Decoder
  const [showVinModal, setShowVinModal] = useState(false);
  const [vinInput, setVinInput] = useState('');
  const [vinLoading, setVinLoading] = useState(false);
  const [vinResult, setVinResult] = useState(null);
  const [vinError, setVinError] = useState('');

  // Create form state
  const [hondaVehicles, setHondaVehicles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [catalogParts, setCatalogParts] = useState([]);
  const [selectedVehicle, setSelectedVehicle] = useState('');
  const [selectedYear, setSelectedYear] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [partSearch, setPartSearch] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [customerAddress, setCustomerAddress] = useState('');
  const [customerId, setCustomerId] = useState('');
  const [customerSearch, setCustomerSearch] = useState('');
  const [customerResults, setCustomerResults] = useState([]);
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const [vehicleInfo, setVehicleInfo] = useState('');
  const [invoiceItems, setInvoiceItems] = useState([]);
  const [notes, setNotes] = useState('');
  const [taxRate, setTaxRate] = useState(7);
  const [discount, setDiscount] = useState(0);
  const [saving, setSaving] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailTo, setEmailTo] = useState('');
  const [emailMessage, setEmailMessage] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailResult, setEmailResult] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [settingsForm, setSettingsForm] = useState({ prefix: 'INV', next_number: 1001, company_name: 'Martinez Auto Parts', company_phone: '1-800-MARTINEZ', company_email: 'martinez@autoparts.com', company_address: '', default_tax_rate: 7.0, default_notes: '' });

  useEffect(() => {
    if (authLoading) return;
    if (!user || user.role !== 'admin') { navigate('/login'); return; }
    fetchInvoices();
    api.get('/catalog/vehicles').then(r => setHondaVehicles(r.data)).catch(() => {});
    api.get('/catalog/categories').then(r => setCategories(r.data)).catch(() => {});
    api.get('/admin/invoice-settings').then(r => { if (r.data.default_tax_rate) setTaxRate(r.data.default_tax_rate); }).catch(() => {});
  }, [user, authLoading, navigate]);

  const fetchInvoices = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/invoices');
      setInvoices(data.invoices || []);
    } catch {} finally { setLoading(false); }
  };

  const fetchParts = useCallback(async () => {
    if (!selectedVehicle || !selectedYear) { setCatalogParts([]); return; }
    try {
      const params = { vehicle: selectedVehicle, year: selectedYear };
      if (selectedCategory) params.category = selectedCategory;
      if (partSearch) params.search = partSearch;
      const { data } = await api.get('/catalog/parts', { params });
      setCatalogParts(data);
    } catch {}
  }, [selectedVehicle, selectedYear, selectedCategory, partSearch]);

  useEffect(() => { fetchParts(); }, [fetchParts]);

  const addPart = (part) => {
    if (invoiceItems.find(i => i.oem_number === part.oem_number)) return;
    setInvoiceItems(prev => [...prev, { part_name: part.part_name, oem_number: part.oem_number, quantity: 1, unit_price: 0, condition: 'new_aftermarket', donor_vin: '' }]);
  };

  const addManualPart = () => {
    setInvoiceItems(prev => [...prev, { part_name: '', oem_number: '', quantity: 1, unit_price: 0, condition: 'new_aftermarket', donor_vin: '' }]);
  };

  const generateVin = async (idx) => {
    const vehicle = selectedVehicle || vehicleInfo.split(' ').slice(1).join(' ');
    const year = selectedYear || parseInt(vehicleInfo);
    if (!vehicle || !year) return;
    try {
      const { data } = await api.get('/generate-vin', { params: { vehicle, year } });
      if (data.vin) updateItem(idx, 'donor_vin', data.vin);
    } catch {}
  };

  const updateItem = (idx, field, val) => setInvoiceItems(prev => prev.map((item, i) => i === idx ? { ...item, [field]: val } : item));
  const removeItem = (idx) => setInvoiceItems(prev => prev.filter((_, i) => i !== idx));

  const subtotal = invoiceItems.reduce((s, i) => s + i.unit_price * i.quantity, 0);
  const taxAmount = subtotal * (taxRate / 100);
  const total = subtotal - discount + taxAmount;

  const resetForm = () => {
    setCustomerName(''); setCustomerEmail(''); setCustomerPhone(''); setCustomerAddress('');
    setCustomerId(''); setCustomerSearch(''); setCustomerResults([]);
    setVehicleInfo(''); setInvoiceItems([]); setNotes(''); setTaxRate(7); setDiscount(0);
    setSelectedVehicle(''); setSelectedYear(''); setSelectedCategory(''); setPartSearch('');
  };

  const searchCustomers = async (q) => {
    setCustomerSearch(q);
    if (q.length < 1) { setCustomerResults([]); setShowCustomerDropdown(false); return; }
    try {
      const { data } = await api.get('/admin/customers-search', { params: { q } });
      setCustomerResults(data);
      setShowCustomerDropdown(true);
    } catch {}
  };

  const selectCustomer = (c) => {
    setCustomerId(c.id);
    setCustomerName(c.name);
    setCustomerEmail(c.email || '');
    setCustomerPhone(c.phone || '');
    setCustomerAddress(c.address || '');
    setCustomerSearch(c.name);
    setShowCustomerDropdown(false);
    // Auto-fill vehicle if customer has one
    if (c.vehicles?.length > 0) {
      const v = c.vehicles[0];
      setVehicleInfo(`${v.year} ${v.make} ${v.model}`);
      // Try to auto-select in catalog
      const vehicleName = `${v.make} ${v.model}`;
      setSelectedVehicle(vehicleName);
      setSelectedYear(String(v.year));
    }
  };

  const handleCreate = async () => {
    if (!customerName || invoiceItems.length === 0 || invoiceItems.some(i => !i.unit_price)) return;
    setSaving(true);
    try {
      const payload = {
        customer_name: customerName, customer_email: customerEmail,
        customer_phone: customerPhone, customer_address: customerAddress,
        customer_id: customerId, vehicle_info: vehicleInfo, items: invoiceItems,
        notes, tax_rate: taxRate, discount
      };
      let data;
      if (editingInvoice) {
        const resp = await api.put(`/admin/invoices/${editingInvoice.id}`, payload);
        data = resp.data;
      } else {
        const resp = await api.post('/admin/invoices', payload);
        data = resp.data;
      }
      setPreviewInvoice(data);
      setEditingInvoice(null);
      setView('preview');
      resetForm();
      fetchInvoices();
    } catch {} finally { setSaving(false); }
  };

  const handleEditInvoice = (inv) => {
    setEditingInvoice(inv);
    setCustomerName(inv.customer_name || '');
    setCustomerEmail(inv.customer_email || '');
    setCustomerPhone(inv.customer_phone || '');
    setCustomerAddress(inv.customer_address || '');
    setCustomerId(inv.customer_id || '');
    setCustomerSearch(inv.customer_name || '');
    setVehicleInfo(inv.vehicle_info || '');
    setInvoiceItems(inv.items || []);
    setNotes(inv.notes || '');
    setTaxRate(inv.tax_rate || 7);
    setDiscount(inv.discount || 0);
    setView('create');
  };

  const handleDecodeVin = async () => {
    if (!vinInput || vinInput.length !== 17) { setVinError('VIN must be 17 characters'); return; }
    setVinLoading(true); setVinError(''); setVinResult(null);
    try {
      const { data } = await api.get(`/decode-vin/${vinInput.trim()}`);
      setVinResult(data);
    } catch (e) {
      setVinError(e.response?.data?.detail || 'Could not decode VIN');
    } finally { setVinLoading(false); }
  };

  const applyVinResult = () => {
    if (vinResult?.vehicle_info) setVehicleInfo(vinResult.vehicle_info);
    if (vinResult?.make && vinResult?.model) {
      const vName = `${vinResult.make} ${vinResult.model}`;
      setSelectedVehicle(vName);
      if (vinResult.year) setSelectedYear(vinResult.year);
    }
    setShowVinModal(false); setVinInput(''); setVinResult(null);
  };

  const handlePrint = () => {
    const el = document.getElementById('invoice-print-content');
    if (!el) return;
    const qrSvgEl = el.querySelector('[data-testid="qr-code-svg"] svg');
    let qrSvgStr = qrSvgEl ? qrSvgEl.outerHTML : '';
    const vToken = previewInvoice?.verification_token || '';
    const vUrl = vToken ? `${window.location.origin}/verify/${vToken}` : '';
    const qrSection = vToken ? `
      <div style="background:linear-gradient(135deg,#0F766E,#0D9488);border-radius:10px;padding:20px;display:flex;align-items:center;gap:20px">
        <div style="background:#fff;border-radius:8px;padding:8px;flex-shrink:0">${qrSvgStr}</div>
        <div style="color:#fff">
          <div style="font-size:15px;font-weight:800;margin-bottom:6px">Scan to Verify This Invoice</div>
          <p style="font-size:11px;color:rgba(255,255,255,0.8);line-height:1.5;margin-bottom:8px">Scan this QR code to verify this invoice is authentic and certified by <strong style="color:#fff">Martinez Auto Parts</strong>.</p>
          <span style="background:rgba(255,255,255,0.2);font-size:10px;font-weight:700;padding:4px 12px;border-radius:20px">Certified Invoice ID: ${vToken.slice(0, 8).toUpperCase()}</span>
        </div>
      </div>` : '';
    const logoImgUrl = window.location.origin + LOGO_URL;
    const phone = previewInvoice?.company_phone || '740-564-8157';
    const email = previewInvoice?.company_email || 'mtzluis90@hotmail.com';
    const invNum = previewInvoice?.invoice_number || '';
    const invDate = previewInvoice?.created_at ? new Date(previewInvoice.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : '';
    const custName = previewInvoice?.customer_name || '';
    const custPhone = previewInvoice?.customer_phone || '';
    const custEmail = previewInvoice?.customer_email || '';
    const custAddr = previewInvoice?.customer_address || '';
    const vehicleInfo = previewInvoice?.vehicle_info || '';
    const condLbl = (c) => c === 'used_oem' ? 'Used OEM' : c === 'refurbished' ? 'Refurbished' : 'New';
    let itemsRows = '';
    (previewInvoice?.items || []).forEach((item, i) => {
      const bg = i % 2 !== 0 ? 'background:#f8f9fa;' : '';
      const amt = (item.unit_price * item.quantity).toFixed(2);
      itemsRows += `<tr style="${bg}"><td style="padding:8px 12px;border-bottom:1px solid #eee;font-size:12px;font-weight:600">${item.part_name}${item.donor_vin ? `<div style="font-size:9px;font-family:monospace;color:#999;margin-top:2px">VIN: ${item.donor_vin}</div>` : ''}</td><td style="padding:8px 12px;border-bottom:1px solid #eee;font-family:monospace;color:#0F766E;font-weight:700;font-size:11px">${item.oem_number}</td><td style="padding:8px 12px;border-bottom:1px solid #eee;font-size:11px;color:#666">${condLbl(item.condition)}</td><td style="padding:8px 12px;border-bottom:1px solid #eee;text-align:center;font-size:12px">${item.quantity}</td><td style="padding:8px 12px;border-bottom:1px solid #eee;text-align:right;font-size:12px">$${item.unit_price?.toFixed(2)}</td><td style="padding:8px 12px;border-bottom:1px solid #eee;text-align:right;font-weight:700;font-size:12px">$${amt}</td></tr>`;
    });
    const subtotal = previewInvoice?.subtotal?.toFixed(2) || '0.00';
    const taxRate = previewInvoice?.tax_rate || 0;
    const taxAmt = previewInvoice?.tax_amount?.toFixed(2) || '0.00';
    const discountAmt = previewInvoice?.discount?.toFixed(2) || '0.00';
    const total = previewInvoice?.total?.toFixed(2) || '0.00';
    const notes = previewInvoice?.notes || '';

    const printHtml = `<!DOCTYPE html><html><head><title>${invNum} - Martinez Auto Parts</title>
<style>
@page{margin:0;size:letter}
@media print{
  @page{margin:0;size:letter}
  html,body{margin:0;padding:0}
  body{-webkit-print-color-adjust:exact;print-color-adjust:exact}
}
*{margin:0;padding:0;box-sizing:border-box}
html,body{width:100%;height:100%}
body{font-family:'Helvetica Neue',Arial,sans-serif;color:#111;font-size:12px}
.page{width:100%;min-height:100vh;padding:14mm;display:flex;flex-direction:column}
.content{flex:1}
.footer-section{margin-top:auto;padding-top:16px}
</style></head><body>
<div class="page">
<div class="content">
<div style="display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:24px;border-bottom:3px solid #0F766E;padding-bottom:18px">
  <div>
    <img src="${logoImgUrl}" alt="Martinez Auto Parts" style="height:80px;width:auto" />
    <div style="margin-top:10px;font-size:11px;color:#888">
      <div>4720 N High St, Columbus, OH 43214</div>
      <div>${phone} | ${email}</div>
      <div style="color:#0F766E;font-weight:600">www.martinez-autoparts.com</div>
    </div>
  </div>
  <div style="text-align:right">
    <div style="font-size:30px;font-weight:900;color:#0F766E;line-height:1">INVOICE</div>
    <div style="font-family:monospace;font-weight:700;color:#444;margin-top:6px;font-size:13px">${invNum}</div>
    <div style="font-size:11px;color:#888;margin-top:3px">${invDate}</div>
  </div>
</div>
<div style="display:flex;justify-content:space-between;margin-bottom:24px">
  <div style="width:50%">
    <div style="font-size:9px;font-weight:700;text-transform:uppercase;letter-spacing:2px;color:#aaa;margin-bottom:6px">Bill To</div>
    <div style="font-weight:700;font-size:14px">${custName}</div>
    ${custPhone ? `<div style="font-size:12px;color:#555;margin-top:2px">${custPhone}</div>` : ''}
    ${custEmail ? `<div style="font-size:12px;color:#555">${custEmail}</div>` : ''}
    ${custAddr ? `<div style="font-size:12px;color:#555">${custAddr}</div>` : ''}
  </div>
  ${vehicleInfo ? `<div style="text-align:right"><div style="font-size:9px;font-weight:700;text-transform:uppercase;letter-spacing:2px;color:#aaa;margin-bottom:6px">Vehicle</div><div style="font-weight:700;font-size:14px">${vehicleInfo}</div></div>` : ''}
</div>
<table style="width:100%;border-collapse:collapse;margin-bottom:20px">
  <thead><tr>
    <th style="background:#0F766E;color:#fff;padding:8px 12px;text-align:left;font-size:9px;text-transform:uppercase;letter-spacing:1px;font-weight:700">Description</th>
    <th style="background:#0F766E;color:#fff;padding:8px 12px;text-align:left;font-size:9px;text-transform:uppercase;letter-spacing:1px;font-weight:700">OEM #</th>
    <th style="background:#0F766E;color:#fff;padding:8px 12px;text-align:left;font-size:9px;text-transform:uppercase;letter-spacing:1px;font-weight:700">Condition</th>
    <th style="background:#0F766E;color:#fff;padding:8px 12px;text-align:center;font-size:9px;text-transform:uppercase;letter-spacing:1px;font-weight:700">Qty</th>
    <th style="background:#0F766E;color:#fff;padding:8px 12px;text-align:right;font-size:9px;text-transform:uppercase;letter-spacing:1px;font-weight:700">Price</th>
    <th style="background:#0F766E;color:#fff;padding:8px 12px;text-align:right;font-size:9px;text-transform:uppercase;letter-spacing:1px;font-weight:700">Amount</th>
  </tr></thead>
  <tbody>${itemsRows}</tbody>
</table>
${notes ? `<div style="padding:12px;background:#f3f4f6;border-radius:6px;margin-bottom:16px"><div style="font-size:9px;font-weight:700;text-transform:uppercase;letter-spacing:2px;color:#aaa;margin-bottom:4px">Notes</div><div style="font-size:11px;color:#333">${notes}</div></div>` : ''}
</div>
<div class="footer-section">
<div style="display:flex;justify-content:flex-end;margin-bottom:20px">
  <div style="width:240px">
    <div style="display:flex;justify-content:space-between;padding:4px 0;font-size:12px"><span style="color:#666">Subtotal</span><span style="font-weight:600">$${subtotal}</span></div>
    ${taxRate > 0 ? `<div style="display:flex;justify-content:space-between;padding:4px 0;font-size:12px"><span style="color:#666">Tax (${taxRate}%)</span><span>$${taxAmt}</span></div>` : ''}
    ${parseFloat(discountAmt) > 0 ? `<div style="display:flex;justify-content:space-between;padding:4px 0;font-size:12px"><span style="color:#666">Discount</span><span style="color:red">-$${discountAmt}</span></div>` : ''}
    <div style="display:flex;justify-content:space-between;padding:8px 0 0;border-top:3px solid #0F766E;margin-top:6px"><span style="font-weight:700;font-size:15px">Total</span><span style="font-weight:900;font-size:20px;color:#0F766E">$${total}</span></div>
  </div>
</div>
${qrSection}
<div style="margin-top:16px;text-align:center;color:#bbb;font-size:10px;border-top:1px solid #e5e7eb;padding-top:12px">
  <div>Martinez Auto Parts LLC</div>
  <div style="margin-top:2px">4720 N High St, Columbus, OH 43214</div>
  <div style="margin-top:2px">${phone} | ${email}</div>
  <div style="margin-top:2px;color:#0F766E;font-weight:600">www.martinez-autoparts.com</div>
  <div style="margin-top:4px;color:#ddd">Thank you for your business!</div>
</div>
</div>
</div>
</body></html>`;

    const w = window.open('about:blank', '_blank', 'width=800,height=1100');
    w.document.open();
    w.document.write(printHtml);
    w.document.close();
    // Wait for images to load, then print
    const img = w.document.querySelector('img');
    const doPrint = () => {
      w.document.title = ' ';
      setTimeout(() => w.print(), 100);
    };
    if (img && !img.complete) {
      img.onload = doPrint;
      img.onerror = doPrint;
    } else {
      setTimeout(doPrint, 400);
    }
  };

  const handleDownloadPDF = () => {
    // Use print dialog's "Save as PDF" option
    handlePrint();
  };

  const handleSendEmail = async () => {
    if (!emailTo || !previewInvoice) return;
    setSendingEmail(true); setEmailResult(null);
    try {
      const { data } = await api.post('/admin/invoices/send-email', { invoice_id: previewInvoice.id, recipient_email: emailTo, message: emailMessage });
      setEmailResult({ success: true, msg: data.message });
      setPreviewInvoice({ ...previewInvoice, status: 'sent' });
    } catch (err) {
      setEmailResult({ success: false, msg: err.response?.data?.detail || 'Failed to send email' });
    } finally { setSendingEmail(false); }
  };

  const loadSettings = async () => {
    try { const { data } = await api.get('/admin/invoice-settings'); setSettingsForm(data); } catch {}
  };

  const saveSettings = async () => {
    try { await api.put('/admin/invoice-settings', settingsForm); setShowSettings(false); } catch {}
  };

  const viewInvoice = (inv) => { setPreviewInvoice(inv); setView('preview'); };

  const veh = hondaVehicles.find(v => v.name === selectedVehicle);

  // Build verification URL using frontend origin
  const verifyUrl = previewInvoice?.verification_token
    ? `${window.location.origin}/verify/${previewInvoice.verification_token}`
    : null;

  if (authLoading) return <div className="max-w-6xl mx-auto px-4 py-12 animate-pulse"><div className="h-8 bg-gray-200 rounded w-48" /></div>;
  if (!user || user.role !== 'admin') return null;

  const condLabel = (c) => c === 'used_oem' ? 'Used OEM' : c === 'refurbished' ? 'Refurbished' : 'New';

  // ============ INVOICE PREVIEW ============
  if (view === 'preview' && previewInvoice) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8" data-testid="invoice-preview-page">
        <div className="flex items-center justify-between mb-6">
          <button onClick={() => setView('list')} className="flex items-center gap-2 text-sm text-gray-600 hover:text-[#0F766E] font-medium" data-testid="back-to-invoices">
            <ChevronLeft size={18} /> Back to Invoices
          </button>
          <div className="flex gap-3">
            <button onClick={() => handleEditInvoice(previewInvoice)} className="bg-blue-600 hover:bg-blue-700 text-white font-bold text-sm py-2.5 px-5 rounded flex items-center gap-2 active:scale-95 transition-all" data-testid="edit-invoice-btn">
              <Edit3 size={16} /> Edit Invoice
            </button>
            <button onClick={handleDownloadPDF} className="bg-[#F97316] hover:bg-[#EA580C] text-white font-bold text-sm py-2.5 px-5 rounded flex items-center gap-2 active:scale-95 transition-all" data-testid="download-pdf-btn">
              <Download size={16} /> Download PDF
            </button>
            <button onClick={handlePrint} className="bg-[#0F766E] hover:bg-[#0D5F59] text-white font-bold text-sm py-2.5 px-5 rounded flex items-center gap-2 active:scale-95 transition-all" data-testid="print-invoice-btn">
              <Printer size={16} /> Print Invoice
            </button>
            <button onClick={() => { setEmailTo(previewInvoice?.customer_email || ''); setEmailMessage(''); setEmailResult(null); setShowEmailModal(true); }} className="bg-gray-700 hover:bg-gray-800 text-white font-bold text-sm py-2.5 px-5 rounded flex items-center gap-2 active:scale-95 transition-all" data-testid="email-invoice-btn">
              <Mail size={16} /> Email
            </button>
          </div>
        </div>

        <div id="invoice-print-content" className="bg-white border rounded-lg shadow-lg p-10" data-testid="invoice-document">
          {/* Header */}
          <div className="hdr flex justify-between items-start mb-9 border-b-[3px] border-[#0F766E] pb-6">
            <div>
              <img src="/logo-light.png" alt="Martinez Auto Parts" className="h-20 w-auto" data-testid="invoice-logo" />
              <div className="mt-3 text-xs text-gray-500 space-y-0.5">
                <p>{previewInvoice.company_phone || '740-564-8157'}</p>
                <p>{previewInvoice.company_email || 'mtzluis90@hotmail.com'}</p>
                {previewInvoice.company_address && <p>{previewInvoice.company_address}</p>}
                <p className="text-[#0F766E] font-semibold">www.martinez-autoparts.com</p>
              </div>
            </div>
            <div className="text-right">
              <h1 className="text-[36px] font-black text-[#0F766E] leading-none" style={{ fontFamily: "'Chivo', sans-serif" }}>INVOICE</h1>
              <p className="text-sm font-mono font-bold text-gray-700 mt-2">{previewInvoice.invoice_number}</p>
              <p className="text-xs text-gray-500 mt-1">Date: {new Date(previewInvoice.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
              <span className={`inline-block mt-2 text-xs font-bold px-3 py-1 rounded-full ${statusColors[previewInvoice.status] || statusColors.draft}`}>
                {previewInvoice.status?.toUpperCase()}
              </span>
            </div>
          </div>

          {/* Bill To + Vehicle */}
          <div className="flex justify-between mb-9">
            <div className="w-1/2">
              <h3 className="text-[10px] font-bold uppercase tracking-[2px] text-gray-400 mb-2">Bill To</h3>
              <p className="font-bold text-gray-900 text-base">{previewInvoice.customer_name}</p>
              {previewInvoice.customer_phone && <p className="text-sm text-gray-600 mt-0.5">{previewInvoice.customer_phone}</p>}
              {previewInvoice.customer_email && <p className="text-sm text-gray-600">{previewInvoice.customer_email}</p>}
              {previewInvoice.customer_address && <p className="text-sm text-gray-600">{previewInvoice.customer_address}</p>}
            </div>
            {previewInvoice.vehicle_info && (
              <div className="text-right">
                <h3 className="text-[10px] font-bold uppercase tracking-[2px] text-gray-400 mb-2">Vehicle</h3>
                <p className="font-bold text-gray-900 text-base">{previewInvoice.vehicle_info}</p>
              </div>
            )}
          </div>

          {/* Items Table */}
          <table className="w-full border-collapse mb-8">
            <thead>
              <tr>
                <th className="bg-[#0F766E] text-white px-4 py-3 text-left text-[10px] font-bold uppercase tracking-wider rounded-tl">Description</th>
                <th className="bg-[#0F766E] text-white px-4 py-3 text-left text-[10px] font-bold uppercase tracking-wider">OEM Number</th>
                <th className="bg-[#0F766E] text-white px-4 py-3 text-left text-[10px] font-bold uppercase tracking-wider">Condition</th>
                <th className="bg-[#0F766E] text-white px-4 py-3 text-center text-[10px] font-bold uppercase tracking-wider">Qty</th>
                <th className="bg-[#0F766E] text-white px-4 py-3 text-right text-[10px] font-bold uppercase tracking-wider">Unit Price</th>
                <th className="bg-[#0F766E] text-white px-4 py-3 text-right text-[10px] font-bold uppercase tracking-wider rounded-tr">Amount</th>
              </tr>
            </thead>
            <tbody>
              {previewInvoice.items?.map((item, i) => (
                <tr key={i} className={i % 2 === 0 ? '' : 'bg-gray-50'}>
                  <td className="px-4 py-3 text-sm font-semibold text-gray-900">
                    {item.part_name}
                    {item.donor_vin && <div className="text-[10px] font-mono text-gray-400 mt-0.5">VIN: {item.donor_vin}</div>}
                  </td>
                  <td className="px-4 py-3 text-sm font-mono text-[#0F766E] font-bold">{item.oem_number}</td>
                  <td className="px-4 py-3 text-xs text-gray-600">{condLabel(item.condition)}</td>
                  <td className="px-4 py-3 text-sm text-center">{item.quantity}</td>
                  <td className="px-4 py-3 text-sm text-right">${item.unit_price?.toFixed(2)}</td>
                  <td className="px-4 py-3 text-sm text-right font-bold">${(item.unit_price * item.quantity).toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Totals */}
          <div className="flex justify-end">
            <div className="w-72 space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-gray-500">Subtotal</span><span className="font-semibold">${previewInvoice.subtotal?.toFixed(2)}</span></div>
              {previewInvoice.tax_rate > 0 && <div className="flex justify-between"><span className="text-gray-500">Tax ({previewInvoice.tax_rate}%)</span><span>${previewInvoice.tax_amount?.toFixed(2)}</span></div>}
              {previewInvoice.discount > 0 && <div className="flex justify-between"><span className="text-gray-500">Discount</span><span className="text-red-600">-${previewInvoice.discount?.toFixed(2)}</span></div>}
              <div className="flex justify-between border-t-[3px] border-[#0F766E] pt-3 mt-3">
                <span className="font-bold text-gray-900 text-lg">Total</span>
                <span className="font-black text-2xl text-[#0F766E]">${previewInvoice.total?.toFixed(2)}</span>
              </div>
            </div>
          </div>

          {/* Notes */}
          {previewInvoice.notes && (
            <div className="mt-10 p-5 bg-gray-50 rounded-lg">
              <h3 className="text-[10px] font-bold uppercase tracking-[2px] text-gray-400 mb-2">Notes</h3>
              <p className="text-sm text-gray-700 leading-relaxed">{previewInvoice.notes}</p>
            </div>
          )}

          {/* QR Code Verification */}
          {verifyUrl && (
            <div className="mt-10 bg-gradient-to-r from-[#0F766E] to-[#0D9488] rounded-xl p-6 text-white">
              <div className="flex items-center gap-6">
                <div className="bg-white rounded-lg p-3 shrink-0" data-testid="qr-code-svg">
                  <QRCodeSVG value={verifyUrl} size={100} level="M" bgColor="#ffffff" fgColor="#0F766E" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <svg viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5 text-white"><path fillRule="evenodd" d="M16.403 12.652a3 3 0 000-5.304 3 3 0 00-3.75-3.751 3 3 0 00-5.305 0 3 3 0 00-3.751 3.75 3 3 0 000 5.305 3 3 0 003.75 3.751 3 3 0 005.305 0 3 3 0 003.751-3.75zm-2.546-4.46a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z" clipRule="evenodd" /></svg>
                    <span className="text-lg font-bold">Scan to Verify This Invoice</span>
                  </div>
                  <p className="text-white/80 text-sm leading-relaxed">Scan this QR code with your phone camera to verify this invoice is authentic and certified by <strong className="text-white">Martinez Auto Parts</strong>.</p>
                  <div className="mt-3 flex items-center gap-3">
                    <span className="bg-white/20 backdrop-blur text-xs font-bold px-3 py-1.5 rounded-full">Certified Invoice</span>
                    <span className="text-xs font-mono text-white/60">ID: {previewInvoice?.verification_token?.slice(0, 8).toUpperCase()}</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Footer */}
          <div className="mt-8 text-center text-gray-400 text-xs border-t pt-5">
            <p className="font-medium">Martinez Auto Parts LLC</p>
            <p className="mt-1">4720 N High St, Columbus, OH 43214</p>
            <p className="mt-1">740-564-8157 | mtzluis90@hotmail.com</p>
            <p className="mt-1 text-[#0F766E] font-semibold">www.martinez-autoparts.com</p>
            <p className="mt-2 text-gray-300">Thank you for your business!</p>
          </div>
        </div>
      </div>
    );
  }

  // ============ CREATE INVOICE ============
  if (view === 'create') {
    return (
      <><div className="max-w-5xl mx-auto px-4 py-8" data-testid="invoice-create-page">
        <div className="flex items-center justify-between mb-6">
          <div>
            <button onClick={() => { setView('list'); setEditingInvoice(null); resetForm(); }} className="flex items-center gap-1 text-sm text-gray-500 hover:text-[#0F766E] mb-2"><ChevronLeft size={16} /> Back</button>
            <h1 className="text-2xl font-bold text-gray-900" style={{ fontFamily: "'Chivo', sans-serif" }}>{editingInvoice ? `Edit Invoice ${editingInvoice.invoice_number}` : 'Create New Invoice'}</h1>
          </div>
        </div>

        <div className="space-y-6">
          {/* Customer Info */}
          <div className="bg-white border rounded-lg p-6">
            <h3 className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-4">Customer Information</h3>
            {/* Customer search / autofill */}
            <div className="relative mb-3">
              <Search size={16} className="absolute left-3 top-3.5 text-gray-400" />
              <input placeholder="Search existing customers or type new name..." value={customerSearch} onChange={e => { searchCustomers(e.target.value); setCustomerName(e.target.value); setCustomerId(''); }}
                onFocus={() => { if (customerResults.length) setShowCustomerDropdown(true); }}
                className="w-full border-2 border-gray-200 rounded pl-10 pr-4 py-3 text-sm focus:border-[#0F766E] focus:outline-none" data-testid="inv-customer-search" />
              {showCustomerDropdown && customerResults.length > 0 && (
                <div className="absolute left-0 right-0 top-full mt-1 bg-white border rounded-lg shadow-xl z-30 max-h-48 overflow-y-auto" data-testid="customer-dropdown">
                  {customerResults.map(c => (
                    <button key={c.id} onClick={() => selectCustomer(c)} className="w-full text-left px-4 py-3 hover:bg-[#0F766E]/5 border-b last:border-0 flex items-start gap-3" data-testid={`customer-option-${c.id}`}>
                      <Users size={16} className="text-[#0F766E] mt-0.5 shrink-0" />
                      <div>
                        <p className="text-sm font-semibold text-gray-900">{c.name} {c.company && <span className="text-xs text-gray-500 font-normal">({c.company})</span>}</p>
                        <p className="text-xs text-gray-500">{[c.phone, c.email].filter(Boolean).join(' | ')}</p>
                        {c.vehicles?.length > 0 && <p className="text-[10px] text-[#0F766E] font-medium mt-0.5">{c.vehicles.map(v => `${v.year} ${v.make} ${v.model}`).join(', ')}</p>}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
            {customerId && <p className="text-xs text-[#0F766E] font-semibold mb-3 flex items-center gap-1"><Users size={12} /> Linked to existing customer: {customerName}</p>}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <input placeholder="Customer Name *" value={customerName} onChange={e => setCustomerName(e.target.value)} className="border-2 border-gray-200 rounded px-4 py-3 text-sm focus:border-[#0F766E] focus:outline-none" data-testid="inv-customer-name" />
              <input placeholder="Phone" value={customerPhone} onChange={e => setCustomerPhone(e.target.value)} className="border-2 border-gray-200 rounded px-4 py-3 text-sm focus:border-[#0F766E] focus:outline-none" data-testid="inv-customer-phone" />
              <input placeholder="Email" value={customerEmail} onChange={e => setCustomerEmail(e.target.value)} className="border-2 border-gray-200 rounded px-4 py-3 text-sm focus:border-[#0F766E] focus:outline-none" data-testid="inv-customer-email" />
              <input placeholder="Address" value={customerAddress} onChange={e => setCustomerAddress(e.target.value)} className="border-2 border-gray-200 rounded px-4 py-3 text-sm focus:border-[#0F766E] focus:outline-none" data-testid="inv-customer-address" />
            </div>
          </div>

          {/* Vehicle Selection */}
          <div className="bg-white border rounded-lg p-6">
            <h3 className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-4">Vehicle - OEM Parts Catalog (All Makes & Models)</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <select value={selectedVehicle} onChange={e => { setSelectedVehicle(e.target.value); setSelectedYear(''); }} className="border-2 border-gray-200 rounded px-4 py-3 text-sm focus:border-[#0F766E] focus:outline-none" data-testid="inv-vehicle-select">
                <option value="">Select Vehicle</option>
                {hondaVehicles.map(v => <option key={v.name} value={v.name}>{v.name}</option>)}
              </select>
              <select value={selectedYear} onChange={e => { setSelectedYear(e.target.value); setVehicleInfo(`${e.target.value} ${selectedVehicle}`); }} disabled={!selectedVehicle} className="border-2 border-gray-200 rounded px-4 py-3 text-sm focus:border-[#0F766E] focus:outline-none disabled:opacity-40" data-testid="inv-year-select">
                <option value="">Select Year</option>
                {veh?.years.map(y => <option key={y} value={y}>{y}</option>)}
              </select>
              <input placeholder="Vehicle info (editable)" value={vehicleInfo} onChange={e => setVehicleInfo(e.target.value)} className="border-2 border-gray-200 rounded px-4 py-3 text-sm focus:border-[#0F766E] focus:outline-none flex-1" data-testid="inv-vehicle-info" />
              <button onClick={() => { setShowVinModal(true); setVinInput(''); setVinResult(null); setVinError(''); }} className="bg-[#0F766E] hover:bg-[#0D5F59] text-white font-bold text-xs py-3 px-4 rounded flex items-center gap-1.5 whitespace-nowrap transition-colors" data-testid="vin-decode-btn">
                <Search size={14} /> Decode VIN
              </button>
            </div>

            {/* Parts Catalog */}
            {selectedVehicle && selectedYear && (
              <div className="mt-5">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-bold text-gray-700">OEM Parts - {selectedYear} {selectedVehicle} <span className="text-[#0F766E]">({catalogParts.length} parts)</span></h4>
                </div>
                <div className="flex gap-2 mb-3">
                  <div className="relative flex-1">
                    <Search size={16} className="absolute left-3 top-3 text-gray-400" />
                    <input placeholder="Search by part name or OEM#..." value={partSearch} onChange={e => setPartSearch(e.target.value)} className="w-full border-2 border-gray-200 rounded pl-9 pr-3 py-2.5 text-sm focus:border-[#0F766E] focus:outline-none" data-testid="inv-part-search" />
                  </div>
                  <select value={selectedCategory} onChange={e => setSelectedCategory(e.target.value)} className="border-2 border-gray-200 rounded px-3 py-2.5 text-sm focus:border-[#0F766E] focus:outline-none" data-testid="inv-category-filter">
                    <option value="">All Categories</option>
                    {categories.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div className="border rounded-lg max-h-56 overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead><tr className="bg-gray-50 text-left text-[10px] font-bold uppercase tracking-wider text-gray-500 sticky top-0">
                      <th className="px-3 py-2.5">Part Name</th><th className="px-3 py-2.5">OEM #</th><th className="px-3 py-2.5">PartsLink</th><th className="px-3 py-2.5">Category</th><th className="px-3 py-2.5 w-16"></th>
                    </tr></thead>
                    <tbody className="divide-y">
                      {catalogParts.map((part, i) => {
                        const added = invoiceItems.some(item => item.oem_number === part.oem_number);
                        return (
                          <tr key={i} className={`hover:bg-[#0F766E]/5 ${added ? 'bg-green-50' : ''}`}>
                            <td className="px-3 py-2.5 font-medium text-gray-900">{part.part_name}</td>
                            <td className="px-3 py-2.5 font-mono text-[#0F766E] font-bold">{part.oem_number}</td>
                            <td className="px-3 py-2.5 text-gray-500">{part.partslink}</td>
                            <td className="px-3 py-2.5 text-gray-500">{part.category}</td>
                            <td className="px-3 py-2.5">
                              {added ? <span className="text-green-600 text-[10px] font-bold">Added</span> : (
                                <button onClick={() => addPart(part)} className="bg-[#F97316] text-white text-[10px] font-bold px-2.5 py-1 rounded hover:bg-[#EA580C] flex items-center gap-1">
                                  <Plus size={10} /> Add
                                </button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                      {catalogParts.length === 0 && <tr><td colSpan="5" className="px-3 py-8 text-center text-gray-400">Select a vehicle and year to see parts</td></tr>}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>

          {/* Invoice Items */}
          <div className="bg-white border rounded-lg p-6">
            <h3 className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-4">Invoice Items ({invoiceItems.length})</h3>
            {invoiceItems.length === 0 ? (
              <div className="text-center py-10 border-2 border-dashed border-gray-200 rounded-lg">
                <FileText size={32} className="mx-auto text-gray-300 mb-2" />
                <p className="text-sm text-gray-400">Select parts from the catalog above or add manually</p>
                <button onClick={addManualPart} className="mt-3 inline-flex items-center gap-1.5 px-4 py-2 bg-[#0F766E] text-white text-xs font-bold rounded hover:bg-[#0D6B63] transition-colors" data-testid="inv-add-manual-btn">
                  <Plus size={14} /> Add Part Manually
                </button>
              </div>
            ) : (
              <div>
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead><tr className="bg-gray-50 text-left text-[10px] font-bold uppercase tracking-wider text-gray-500">
                    <th className="px-3 py-2.5">Part</th><th className="px-3 py-2.5">OEM #</th><th className="px-3 py-2.5 w-24">Condition</th><th className="px-3 py-2.5">Donor VIN</th><th className="px-3 py-2.5 w-16">Qty</th><th className="px-3 py-2.5 w-24">Price</th><th className="px-3 py-2.5 w-20 text-right">Total</th><th className="px-3 py-2.5 w-8"></th>
                  </tr></thead>
                  <tbody className="divide-y">
                    {invoiceItems.map((item, i) => (
                      <tr key={i} className="group">
                        <td className="px-3 py-2"><input type="text" value={item.part_name} onChange={e => updateItem(i, 'part_name', e.target.value)} placeholder="Part name..." className="w-full border rounded px-2 py-1.5 text-xs font-medium text-gray-900 focus:border-[#0F766E] focus:outline-none" data-testid={`inv-part-name-${i}`} /></td>
                        <td className="px-3 py-2"><input type="text" value={item.oem_number} onChange={e => updateItem(i, 'oem_number', e.target.value)} placeholder="OEM#..." className="w-full border rounded px-2 py-1.5 font-mono text-xs text-[#0F766E] font-bold focus:border-[#0F766E] focus:outline-none" data-testid={`inv-oem-${i}`} /></td>
                        <td className="px-3 py-2"><select value={item.condition} onChange={e => updateItem(i, 'condition', e.target.value)} className="border rounded px-2 py-1.5 text-xs w-full"><option value="new_aftermarket">New</option><option value="used_oem">Used OEM</option><option value="refurbished">Refurbished</option></select></td>
                        <td className="px-3 py-2">
                          <div className="flex items-center gap-1">
                            <input type="text" value={item.donor_vin || ''} onChange={e => updateItem(i, 'donor_vin', e.target.value)} placeholder="Donor VIN..." className="w-full border rounded px-2 py-1.5 font-mono text-[10px] text-gray-700 focus:border-[#0F766E] focus:outline-none" data-testid={`inv-vin-${i}`} />
                            <button onClick={() => generateVin(i)} title="Generate VIN" className="shrink-0 w-7 h-7 flex items-center justify-center bg-[#F97316] hover:bg-[#EA580C] text-white rounded text-[10px] font-bold transition-colors" data-testid={`inv-gen-vin-${i}`}>VIN</button>
                          </div>
                        </td>
                        <td className="px-3 py-2"><input type="number" min="1" value={item.quantity} onChange={e => updateItem(i, 'quantity', parseInt(e.target.value) || 1)} className="border rounded px-2 py-1.5 w-14 text-xs text-center" /></td>
                        <td className="px-3 py-2">
                          <div className="relative">
                            <span className="absolute left-2 top-2 text-xs text-gray-400">$</span>
                            <input type="number" step="0.01" min="0" value={item.unit_price || ''} onChange={e => updateItem(i, 'unit_price', parseFloat(e.target.value) || 0)} placeholder="0.00" className="border rounded pl-5 pr-2 py-1.5 w-full text-xs" data-testid={`inv-price-${i}`} />
                          </div>
                        </td>
                        <td className="px-3 py-2 text-right font-bold text-[#0F766E] text-xs">${(item.unit_price * item.quantity).toFixed(2)}</td>
                        <td className="px-3 py-1"><button onClick={() => removeItem(i)} className="text-gray-400 hover:text-red-500 p-1"><Trash2 size={14} /></button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <button onClick={addManualPart} className="mt-3 inline-flex items-center gap-1.5 px-4 py-2 border-2 border-dashed border-gray-300 text-gray-500 text-xs font-bold rounded hover:border-[#0F766E] hover:text-[#0F766E] transition-colors w-full justify-center" data-testid="inv-add-manual-btn-bottom">
                <Plus size={14} /> Add Another Part Manually
              </button>
              </div>
            )}
          </div>

          {/* Totals & Notes */}
          {invoiceItems.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-white border rounded-lg p-6">
                <h3 className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-3">Notes / Payment Terms</h3>
                <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={3} placeholder="Net 30 payment terms, insurance claim #, special instructions..." className="w-full border-2 border-gray-200 rounded px-4 py-3 text-sm focus:border-[#0F766E] focus:outline-none" data-testid="inv-notes" />
              </div>
              <div className="bg-white border rounded-lg p-6 space-y-3 text-sm">
                <div className="flex justify-between"><span className="text-gray-500">Subtotal</span><span className="font-semibold text-gray-900">${subtotal.toFixed(2)}</span></div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-500">Tax Rate</span>
                  <div className="flex items-center gap-1"><input type="number" step="0.1" min="0" max="20" value={taxRate} onChange={e => setTaxRate(parseFloat(e.target.value) || 0)} className="border rounded px-2 py-1 w-16 text-xs text-right" data-testid="inv-tax-rate" /><span className="text-xs text-gray-500">%</span></div>
                </div>
                {taxRate > 0 && <div className="flex justify-between"><span className="text-gray-500">Tax Amount</span><span>${taxAmount.toFixed(2)}</span></div>}
                <div className="flex justify-between items-center">
                  <span className="text-gray-500">Discount</span>
                  <div className="flex items-center gap-1"><span className="text-xs text-gray-500">$</span><input type="number" step="0.01" min="0" value={discount} onChange={e => setDiscount(parseFloat(e.target.value) || 0)} className="border rounded px-2 py-1 w-20 text-xs text-right" data-testid="inv-discount" /></div>
                </div>
                <div className="flex justify-between border-t-[3px] border-[#0F766E] pt-3 mt-2">
                  <span className="font-bold text-gray-900 text-lg">Total</span>
                  <span className="font-black text-2xl text-[#0F766E]" data-testid="inv-total">${total.toFixed(2)}</span>
                </div>
              </div>
            </div>
          )}

          {/* Submit */}
          <div className="flex gap-3">
            <button onClick={() => { resetForm(); setEditingInvoice(null); setView('list'); }} className="px-6 py-3 border-2 rounded-lg text-sm font-semibold text-gray-600 hover:bg-gray-50">Cancel</button>
            <button onClick={handleCreate} disabled={saving || !customerName || invoiceItems.length === 0 || invoiceItems.some(i => !i.unit_price)}
              className="flex-1 bg-[#0F766E] hover:bg-[#0D5F59] text-white font-bold py-3 rounded-lg transition-all active:scale-[0.98] disabled:opacity-40 flex items-center justify-center gap-2 text-base"
              data-testid="submit-invoice-btn">
              <FileText size={18} /> {saving ? 'Saving...' : editingInvoice ? 'Save Changes' : 'Create Invoice'}
            </button>
          </div>
        </div>
      </div>

      {/* VIN Decoder Modal */}
      {showVinModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="vin-modal">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg">
            <div className="flex items-center justify-between px-6 py-4 border-b bg-[#0F766E] rounded-t-xl">
              <h3 className="font-bold text-white flex items-center gap-2"><Search size={18} /> VIN Decoder</h3>
              <button onClick={() => setShowVinModal(false)} className="text-white/70 hover:text-white"><X size={20} /></button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-2">Enter VIN Number (17 characters)</label>
                <div className="flex gap-2">
                  <input value={vinInput} onChange={e => setVinInput(e.target.value.toUpperCase().replace(/[^A-HJ-NPR-Z0-9]/g, '').slice(0, 17))} placeholder="e.g. 19XFC2F59GE000001" maxLength={17}
                    className="flex-1 border-2 border-gray-200 rounded px-4 py-3 text-sm font-mono tracking-wider focus:border-[#0F766E] focus:outline-none uppercase" data-testid="vin-input-create"
                    onKeyDown={e => { if (e.key === 'Enter') handleDecodeVin(); }} />
                  <button onClick={handleDecodeVin} disabled={vinLoading || vinInput.length !== 17}
                    className="bg-[#F97316] hover:bg-[#EA580C] text-white font-bold px-6 py-3 rounded text-sm disabled:opacity-40 transition-colors" data-testid="vin-decode-submit-create">
                    {vinLoading ? 'Decoding...' : 'Decode'}
                  </button>
                </div>
                <p className="text-xs text-gray-400 mt-1">{vinInput.length}/17 characters</p>
              </div>
              {vinError && <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3 rounded">{vinError}</div>}
              {vinResult && (
                <div className="bg-[#0F766E]/5 border-2 border-[#0F766E]/20 rounded-lg p-5 space-y-3" data-testid="vin-result-create">
                  <div className="flex items-center justify-between">
                    <h4 className="font-black text-lg text-[#0F766E]">{vinResult.vehicle_info || 'Vehicle Found'}</h4>
                    <span className="bg-green-100 text-green-700 text-xs font-bold px-3 py-1 rounded-full">Decoded</span>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    {vinResult.year && <div><span className="text-gray-500">Year:</span> <strong>{vinResult.year}</strong></div>}
                    {vinResult.make && <div><span className="text-gray-500">Make:</span> <strong>{vinResult.make}</strong></div>}
                    {vinResult.model && <div><span className="text-gray-500">Model:</span> <strong>{vinResult.model}</strong></div>}
                    {vinResult.trim && <div><span className="text-gray-500">Trim:</span> <strong>{vinResult.trim}</strong></div>}
                    {vinResult.body_class && <div><span className="text-gray-500">Body:</span> <strong>{vinResult.body_class}</strong></div>}
                    {vinResult.engine_cylinders && <div><span className="text-gray-500">Engine:</span> <strong>{vinResult.engine_cylinders} cyl</strong></div>}
                    {vinResult.drive_type && <div><span className="text-gray-500">Drive:</span> <strong>{vinResult.drive_type}</strong></div>}
                    {vinResult.fuel_type && <div><span className="text-gray-500">Fuel:</span> <strong>{vinResult.fuel_type}</strong></div>}
                    {vinResult.transmission && <div><span className="text-gray-500">Trans:</span> <strong>{vinResult.transmission}</strong></div>}
                    {vinResult.doors && <div><span className="text-gray-500">Doors:</span> <strong>{vinResult.doors}</strong></div>}
                  </div>
                  <div className="text-xs font-mono text-gray-400 bg-white rounded px-3 py-2">VIN: {vinResult.vin}</div>
                  <button onClick={applyVinResult} className="w-full bg-[#0F766E] hover:bg-[#0D5F59] text-white font-bold py-3 rounded text-sm transition-colors" data-testid="vin-apply-btn-create">
                    Use This Vehicle Info
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
    );
  }

  // ============ INVOICE LIST ============
  return (
    <div className="max-w-6xl mx-auto px-4 py-8" data-testid="invoices-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <Link to="/admin" className="text-sm text-gray-500 hover:text-[#0F766E] flex items-center gap-1 mb-1"><ChevronLeft size={14} /> Admin Dashboard</Link>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900" style={{ fontFamily: "'Chivo', sans-serif" }}>Invoices</h1>
          <p className="text-sm text-gray-500 mt-1">{invoices.length} invoice{invoices.length !== 1 ? 's' : ''} total</p>
        </div>
        <div className="flex gap-3">
          <button onClick={() => { loadSettings(); setShowSettings(true); }} className="border-2 border-gray-300 text-gray-600 font-semibold py-3 px-5 rounded-lg flex items-center gap-2 hover:bg-gray-50 text-sm" data-testid="invoice-settings-btn">
            <Settings size={16} /> Settings
          </button>
          <button onClick={() => setView('create')} className="bg-[#F97316] hover:bg-[#EA580C] text-white font-bold py-3 px-6 rounded-lg flex items-center gap-2 transition-all active:scale-95 text-sm" data-testid="new-invoice-btn">
            <Plus size={18} /> New Invoice
          </button>
        </div>
      </div>

      <div className="bg-white border rounded-lg overflow-hidden shadow-sm">
        {loading ? (
          <div className="p-8 animate-pulse space-y-3">{[1,2,3].map(i => <div key={i} className="h-16 bg-gray-100 rounded" />)}</div>
        ) : invoices.length === 0 ? (
          <div className="text-center py-16">
            <FileText size={48} className="mx-auto text-gray-300 mb-4" />
            <h3 className="text-lg font-bold text-gray-900 mb-2">No invoices yet</h3>
            <p className="text-gray-500 text-sm mb-6">Create your first professional invoice with Honda OEM parts</p>
            <button onClick={() => setView('create')} className="bg-[#F97316] hover:bg-[#EA580C] text-white font-bold py-2.5 px-6 rounded-lg inline-flex items-center gap-2">
              <Plus size={16} /> Create First Invoice
            </button>
          </div>
        ) : (
          <table className="w-full text-sm" data-testid="invoices-table">
            <thead><tr className="bg-gray-50 text-left text-[10px] font-bold uppercase tracking-wider text-gray-500">
              <th className="px-5 py-3">Invoice #</th><th className="px-5 py-3">Customer</th><th className="px-5 py-3">Vehicle</th><th className="px-5 py-3">Items</th><th className="px-5 py-3">Total</th><th className="px-5 py-3">Status</th><th className="px-5 py-3">Date</th><th className="px-5 py-3 w-24">Actions</th>
            </tr></thead>
            <tbody className="divide-y">
              {invoices.map(inv => (
                <tr key={inv.id} className="hover:bg-gray-50" data-testid={`invoice-row-${inv.id}`}>
                  <td className="px-5 py-4 font-mono font-bold text-[#0F766E]">{inv.invoice_number}</td>
                  <td className="px-5 py-4"><p className="font-semibold text-gray-900">{inv.customer_name}</p>{inv.customer_phone && <p className="text-xs text-gray-500">{inv.customer_phone}</p>}</td>
                  <td className="px-5 py-4 text-gray-600">{inv.vehicle_info || '-'}</td>
                  <td className="px-5 py-4 text-gray-600">{inv.items?.length || 0} parts</td>
                  <td className="px-5 py-4 font-black text-[#0F766E] text-base">${inv.total?.toFixed(2)}</td>
                  <td className="px-5 py-4">
                    <span className={`text-xs font-bold px-3 py-1.5 rounded-full ${statusColors[inv.status] || statusColors.draft}`}>
                      {inv.status?.charAt(0).toUpperCase() + inv.status?.slice(1)}
                    </span>
                  </td>
                  <td className="px-5 py-4 text-gray-500 text-xs">{new Date(inv.created_at).toLocaleDateString()}</td>
                  <td className="px-5 py-4">
                    <div className="flex gap-1">
                      <button onClick={() => viewInvoice(inv)} className="p-2 text-gray-400 hover:text-[#0F766E] hover:bg-[#0F766E]/10 rounded transition-colors" title="View & Print" data-testid={`view-invoice-${inv.id}`}>
                        <Eye size={16} />
                      </button>
                      <button onClick={() => handleEditInvoice(inv)} className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors" title="Edit Invoice" data-testid={`edit-invoice-${inv.id}`}>
                        <Edit3 size={16} />
                      </button>
                      <button onClick={() => { setPreviewInvoice(inv); setView('preview'); setTimeout(handlePrint, 500); }} className="p-2 text-gray-400 hover:text-[#F97316] hover:bg-[#F97316]/10 rounded transition-colors" title="Print">
                        <Printer size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Email Invoice Modal */}
      {showEmailModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="email-modal">
          <div className="bg-white rounded-lg shadow-2xl w-full max-w-md">
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <h3 className="font-bold text-gray-900 flex items-center gap-2"><Mail size={18} className="text-blue-600" /> Email Invoice</h3>
              <button onClick={() => setShowEmailModal(false)} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-1">Recipient Email *</label>
                <input type="email" value={emailTo} onChange={e => setEmailTo(e.target.value)} placeholder="customer@email.com" className="w-full border-2 border-gray-200 rounded px-4 py-3 text-sm focus:border-blue-500 focus:outline-none" data-testid="email-to-input" />
              </div>
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-1">Message (optional)</label>
                <textarea value={emailMessage} onChange={e => setEmailMessage(e.target.value)} rows={3} placeholder="Please find your invoice attached..." className="w-full border-2 border-gray-200 rounded px-4 py-3 text-sm focus:border-blue-500 focus:outline-none" data-testid="email-message-input" />
              </div>
              {emailResult && (
                <div className={`text-sm px-4 py-3 rounded ${emailResult.success ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'}`} data-testid="email-result">
                  {emailResult.msg}
                </div>
              )}
              <div className="flex gap-3 pt-2">
                <button onClick={() => setShowEmailModal(false)} className="px-5 py-2.5 border rounded text-sm font-medium text-gray-600 hover:bg-gray-50">Cancel</button>
                <button onClick={handleSendEmail} disabled={sendingEmail || !emailTo} className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-bold py-2.5 rounded flex items-center justify-center gap-2 disabled:opacity-40" data-testid="send-email-btn">
                  <Mail size={16} /> {sendingEmail ? 'Sending...' : 'Send Invoice'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Invoice Settings Modal */}
      {showSettings && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="settings-modal">
          <div className="bg-white rounded-lg shadow-2xl w-full max-w-lg">
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <h3 className="font-bold text-gray-900 flex items-center gap-2"><Settings size={18} className="text-[#0F766E]" /> Invoice Settings</h3>
              <button onClick={() => setShowSettings(false)} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-1">Invoice Prefix</label>
                  <input value={settingsForm.prefix} onChange={e => setSettingsForm(p => ({...p, prefix: e.target.value}))} className="w-full border-2 border-gray-200 rounded px-3 py-2.5 text-sm focus:border-[#0F766E] focus:outline-none" data-testid="settings-prefix" />
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-1">Next Invoice Number</label>
                  <input type="number" value={settingsForm.next_number} onChange={e => setSettingsForm(p => ({...p, next_number: parseInt(e.target.value) || 1}))} className="w-full border-2 border-gray-200 rounded px-3 py-2.5 text-sm focus:border-[#0F766E] focus:outline-none" data-testid="settings-next-number" />
                </div>
              </div>
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-1">Company Name</label>
                <input value={settingsForm.company_name} onChange={e => setSettingsForm(p => ({...p, company_name: e.target.value}))} className="w-full border-2 border-gray-200 rounded px-3 py-2.5 text-sm focus:border-[#0F766E] focus:outline-none" data-testid="settings-company-name" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-1">Phone</label>
                  <input value={settingsForm.company_phone} onChange={e => setSettingsForm(p => ({...p, company_phone: e.target.value}))} className="w-full border-2 border-gray-200 rounded px-3 py-2.5 text-sm focus:border-[#0F766E] focus:outline-none" data-testid="settings-company-phone" />
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-1">Email</label>
                  <input value={settingsForm.company_email} onChange={e => setSettingsForm(p => ({...p, company_email: e.target.value}))} className="w-full border-2 border-gray-200 rounded px-3 py-2.5 text-sm focus:border-[#0F766E] focus:outline-none" data-testid="settings-company-email" />
                </div>
              </div>
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-1">Address</label>
                <input value={settingsForm.company_address} onChange={e => setSettingsForm(p => ({...p, company_address: e.target.value}))} placeholder="123 Main St, City, State ZIP" className="w-full border-2 border-gray-200 rounded px-3 py-2.5 text-sm focus:border-[#0F766E] focus:outline-none" data-testid="settings-company-address" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-1">Default Tax Rate (%)</label>
                  <input type="number" step="0.1" value={settingsForm.default_tax_rate} onChange={e => setSettingsForm(p => ({...p, default_tax_rate: parseFloat(e.target.value) || 0}))} className="w-full border-2 border-gray-200 rounded px-3 py-2.5 text-sm focus:border-[#0F766E] focus:outline-none" data-testid="settings-default-tax" />
                </div>
              </div>
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-1">Default Invoice Notes</label>
                <textarea value={settingsForm.default_notes} onChange={e => setSettingsForm(p => ({...p, default_notes: e.target.value}))} rows={2} placeholder="Net 30, payment terms..." className="w-full border-2 border-gray-200 rounded px-3 py-2.5 text-sm focus:border-[#0F766E] focus:outline-none" data-testid="settings-default-notes" />
              </div>
              <p className="text-xs text-gray-400">Preview: Next invoice will be <strong>{settingsForm.prefix}-{String(settingsForm.next_number).padStart(5,'0')}</strong></p>
              <div className="flex gap-3 pt-2">
                <button onClick={() => setShowSettings(false)} className="px-5 py-2.5 border rounded text-sm font-medium text-gray-600 hover:bg-gray-50">Cancel</button>
                <button onClick={saveSettings} className="flex-1 bg-[#0F766E] hover:bg-[#0D5F59] text-white font-bold py-2.5 rounded" data-testid="save-settings-btn">Save Settings</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
